(() => {
  'use strict';

  const money = (cents, currencySymbol = '$') => {
    const amount = Number(cents || 0) / 100;
    const value = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
    return `${currencySymbol}${value}`;
  };

  class FemLymphaticPage {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.basePrice = Number(root.dataset.basePrice || 0);
      this.baseComparePrice = Number(root.dataset.baseComparePrice || 0);
      this.currencySymbol = root.dataset.currencySymbol || '$';
      this.mainForm = root.querySelector('[data-fem-product-form]');
      this.submitButton = root.querySelector('[data-fem-main-submit]');
      this.submitPrice = root.querySelector('[data-fem-submit-price]');
      this.currentPrice = root.querySelector('.fem-pdp-price-current');
      this.comparePrice = root.querySelector('.fem-pdp-price-compare');
      this.sticky = root.querySelector('[data-fem-sticky-atc]');
      this.stickyPrice = root.querySelector('[data-fem-sticky-price]');
      this.galleryTrack = root.querySelector('[data-fem-media-track]');
      this.gallerySlides = Array.from(root.querySelectorAll('[data-fem-media-slide]'));
      this.galleryThumbs = Array.from(root.querySelectorAll('[data-fem-media-thumb]'));
      this.currentSlide = 0;
      this.kachingObserver = null;
      this.init();
    }

    init() {
      this.applyChromeClasses();
      this.initEmbeddedHeader();
      this.initGallery();
      this.initVariantSelect();
      this.initKachingIntegration();
      this.initStickyAtc();
      this.initTestimonialControls();
      this.initScrollButtons();
      this.initReadMore();
      this.refreshPricesFromKaching();
    }

    applyChromeClasses() {
      document.body.classList.add('fem-lymphatic-active');
      if (this.root.dataset.hideThemeHeader === 'true') {
        document.body.classList.add('fem-lymphatic-hide-header');
      }
      if (this.root.dataset.hideThemeFooter === 'true') {
        document.body.classList.add('fem-lymphatic-hide-footer');
      }
    }


    initEmbeddedHeader() {
      const cartButton = this.root.querySelector('[data-fem-cart-toggle]');
      if (!cartButton) return;
      cartButton.addEventListener('click', () => {
        const themeCartTrigger = Array.from(document.querySelectorAll('#cart-icon-bubble, [data-cart-icon-bubble]'))
          .find((node) => !this.root.contains(node));
        if (themeCartTrigger) {
          themeCartTrigger.click();
          return;
        }
        window.location.href = cartButton.dataset.cartUrl || '/cart';
      });
    }

    initGallery() {
      if (!this.galleryTrack || this.gallerySlides.length < 1) return;

      const goTo = (index, behavior = 'smooth') => {
        const safeIndex = Math.max(0, Math.min(index, this.gallerySlides.length - 1));
        const slide = this.gallerySlides[safeIndex];
        if (!slide) return;
        this.galleryTrack.scrollTo({ left: slide.offsetLeft, behavior });
        this.setActiveSlide(safeIndex);
      };

      this.galleryThumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => goTo(index));
      });

      const prev = this.root.querySelector('[data-fem-gallery-prev]');
      const next = this.root.querySelector('[data-fem-gallery-next]');
      prev?.addEventListener('click', () => goTo(this.currentSlide - 1));
      next?.addEventListener('click', () => goTo(this.currentSlide + 1));

      let raf = 0;
      this.galleryTrack.addEventListener('scroll', () => {
        window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(() => {
          const left = this.galleryTrack.scrollLeft;
          let closest = 0;
          let distance = Infinity;
          this.gallerySlides.forEach((slide, index) => {
            const delta = Math.abs(slide.offsetLeft - left);
            if (delta < distance) {
              distance = delta;
              closest = index;
            }
          });
          this.setActiveSlide(closest);
        });
      }, { passive: true });

      this.setActiveSlide(0);
    }

    setActiveSlide(index) {
      this.currentSlide = index;
      this.galleryThumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const prev = this.root.querySelector('[data-fem-gallery-prev]');
      const next = this.root.querySelector('[data-fem-gallery-next]');
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= this.gallerySlides.length - 1;
    }

    initVariantSelect() {
      const select = this.root.querySelector('[data-fem-variant-select]');
      if (!select || !this.mainForm) return;

      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const variantInput = this.mainForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = option.value;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.basePrice = Number(option.dataset.price || this.basePrice);
        this.baseComparePrice = Number(option.dataset.comparePrice || this.baseComparePrice);
        this.root.dataset.basePrice = String(this.basePrice);
        this.root.dataset.baseComparePrice = String(this.baseComparePrice);
        this.updateDisplayedPrice(this.basePrice);
        window.setTimeout(() => this.refreshPricesFromKaching(), 120);

        const mediaId = option.dataset.mediaId;
        if (mediaId) {
          const targetIndex = this.gallerySlides.findIndex((slide) => slide.dataset.mediaId === mediaId);
          if (targetIndex >= 0 && this.galleryTrack) {
            this.galleryTrack.scrollTo({ left: this.gallerySlides[targetIndex].offsetLeft, behavior: 'smooth' });
          }
        }
      });
    }


    initKachingIntegration() {
      const detect = () => {
        const bundle = this.root.querySelector('kaching-bundle, kaching-bundles-block, .kaching-bundles__block');
        if (!bundle) return false;
        this.root.classList.add('is-kaching-ready');
        this.refreshPricesFromKaching();
        return true;
      };

      if (detect()) {
        this.observeKaching();
        return;
      }

      let checks = 0;
      const timer = window.setInterval(() => {
        checks += 1;
        if (detect() || checks > 60) {
          window.clearInterval(timer);
          if (this.root.classList.contains('is-kaching-ready')) this.observeKaching();
        }
      }, 250);
    }

    observeKaching() {
      const bundle = this.root.querySelector('kaching-bundle, kaching-bundles-block, .kaching-bundles__block');
      if (!bundle || !('MutationObserver' in window)) return;

      this.kachingObserver?.disconnect();
      this.kachingObserver = new MutationObserver(() => this.refreshPricesFromKaching());
      this.kachingObserver.observe(bundle, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'checked', 'aria-checked']
      });

      bundle.addEventListener('click', () => window.setTimeout(() => this.refreshPricesFromKaching(), 80));
      bundle.addEventListener('change', () => window.setTimeout(() => this.refreshPricesFromKaching(), 80));
    }

    refreshPricesFromKaching() {
      const selected = this.root.querySelector('.kaching-bundles__bar--selected') ||
        this.root.querySelector('kaching-bundle input[type="radio"]:checked')?.closest('.kaching-bundles__bar');
      if (!selected) return;

      const price = selected.querySelector('.kaching-bundles__bar-price')?.textContent?.trim();
      const compare = selected.querySelector('.kaching-bundles__bar-full-price')?.textContent?.trim();
      if (price) {
        if (this.currentPrice) this.currentPrice.textContent = price;
        if (this.submitPrice) this.submitPrice.textContent = price;
        if (this.stickyPrice) this.stickyPrice.textContent = price;
      }
      if (this.comparePrice) {
        if (compare && compare !== price) {
          this.comparePrice.textContent = compare;
          this.comparePrice.hidden = false;
        } else {
          this.comparePrice.hidden = true;
        }
      }
    }

    updateDisplayedPrice(cents) {
      const value = money(cents, this.currencySymbol);
      if (this.currentPrice) this.currentPrice.textContent = value;
      if (this.submitPrice) this.submitPrice.textContent = value;
      if (this.stickyPrice) this.stickyPrice.textContent = value;
    }

    initStickyAtc() {
      if (!this.sticky || !this.submitButton) return;
      const trigger = this.root.querySelector('[data-fem-sticky-trigger]') || this.submitButton;

      const update = () => {
        const rect = trigger.getBoundingClientRect();
        const show = rect.bottom < 0 || rect.top > window.innerHeight;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();

      this.sticky.querySelector('[data-fem-sticky-button]')?.addEventListener('click', () => {
        const activeKaching = this.root.querySelector('kaching-bundle, .kaching-bundles__block');
        if (activeKaching && activeKaching.getBoundingClientRect().top > window.innerHeight) {
          activeKaching.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (this.submitButton.disabled) {
          this.mainForm?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.submitButton.click();
      });
    }

    initTestimonialControls() {
      document.querySelectorAll('[data-fem-testimonials]').forEach((container) => {
        if (container.dataset.femControlsBound === 'true') return;
        const track = container.querySelector('[data-fem-testimonial-track]');
        if (!track) return;
        container.dataset.femControlsBound = 'true';
        container.querySelector('[data-fem-testimonial-prev]')?.addEventListener('click', () => {
          track.scrollBy({ left: -Math.max(280, track.clientWidth * 0.72), behavior: 'smooth' });
        });
        container.querySelector('[data-fem-testimonial-next]')?.addEventListener('click', () => {
          track.scrollBy({ left: Math.max(280, track.clientWidth * 0.72), behavior: 'smooth' });
        });
      });
    }

    initScrollButtons() {
      document.querySelectorAll('[data-fem-scroll-to-product]').forEach((button) => {
        if (button.dataset.femBound === 'true') return;
        button.dataset.femBound = 'true';
        button.addEventListener('click', (event) => {
          const targetId = button.dataset.targetSection || this.sectionId;
          const target = document.querySelector(`[data-section-id="${targetId}"] [data-fem-product-form]`) ||
            document.querySelector('.fem-lymphatic-page [data-fem-product-form]');
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }

    initReadMore() {
      document.querySelectorAll('[data-fem-review-more]').forEach((button) => {
        if (button.dataset.femBound === 'true') return;
        button.dataset.femBound = 'true';
        button.addEventListener('click', () => {
          const text = button.closest('.fem-testimonial-body')?.querySelector('.fem-testimonial-text');
          if (!text) return;
          const expanded = text.classList.toggle('is-expanded');
          button.textContent = expanded ? (button.dataset.lessLabel || 'Read Less') : (button.dataset.moreLabel || 'Read More');
          button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
      });
    }
  }

  const boot = (scope = document) => {
    if (document.querySelector('.fem-lymphatic-page')) document.body.classList.add('fem-lymphatic-active');
    scope.querySelectorAll('.fem-lymphatic-page[data-fem-lymphatic-page]').forEach((root) => {
      if (root.dataset.femInitialized === 'true') return;
      root.dataset.femInitialized = 'true';
      new FemLymphaticPage(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
